import { db } from "@/firebase";
import { collection, query, where, getDocs } from "firebase/firestore";

export interface Statistics {
  totalUsers: number;
  totalTutors: number;
  totalStudents: number;
  averageRating: number;
}

class StatisticsService {
  private cache: Statistics | null = null;
  private cacheTimestamp: number = 0;
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  /**
   * Get real-time statistics from database
   * STRICTLY MATCHES DashboardView.vue activeApprovedUsers calculation
   */
  async getStatistics(): Promise<Statistics> {
    try {
      // Fetch ALL users (no pre-filtering to match DashboardView logic exactly)
      const usersRef = collection(db, "users");
      const usersSnapshot = await getDocs(usersRef);

      // MATCH DashboardView activeApprovedUsers calculation EXACTLY
      let totalUsers = 0;
      let totalTutors = 0;
      let totalStudents = 0;
      let totalRating = 0;
      let ratingCount = 0;

      usersSnapshot.forEach((doc) => {
        const data = doc.data();

        // Skip deleted users (match dashboard behavior)
        if (data.status === "deleted") {
          return;
        }

        const userType = (data.userType || data.role)?.toLowerCase();

        // Skip users without userType/role (match dashboard behavior)
        if (!userType || userType === "undefined" || userType === "null") {
          return;
        }

        const appStatus = data.applicationStatus?.toLowerCase();
        const accountStatus = data.status?.toLowerCase();
        const isApproved = appStatus === "approved" || !data.applicationStatus;

        // MATCH DashboardView activeApprovedUsers calculation EXACTLY
        // Replicate userService transformation logic to determine if user is "Active"
        let willBeActive = false;
        if (data.status) {
          // If status field exists, check if it's "active"
          willBeActive = accountStatus === "active";
        } else if (data.isVerified === true) {
          // If no status but isVerified, transformation will make it "Active"
          willBeActive = true;
        }

        // Count Total Users: approved AND will be "Active" after transformation
        if (isApproved && willBeActive) {
          totalUsers++;
        }

        // Count tutors: all approved tutors (regardless of account status)
        if (userType === "tutor" && appStatus === "approved") {
          totalTutors++;
        }

        // Count students: all approved students (regardless of account status)
        if (userType === "student" && appStatus === "approved") {
          totalStudents++;
        }

        // Calculate average rating from approved, active tutors using averageRating field
        if (userType === "tutor" && appStatus === "approved" && accountStatus === "active") {
          if (
            data.averageRating &&
            typeof data.averageRating === "number" &&
            data.averageRating > 0
          ) {
            totalRating += data.averageRating;
            ratingCount++;
          }
        }
      });

      const averageRating =
        ratingCount > 0 ? Math.round((totalRating / ratingCount) * 10) / 10 : 4.9;

      const statistics: Statistics = {
        totalUsers,
        totalTutors,
        totalStudents,
        averageRating,
      };

      return statistics;
    } catch (error) {
      // Return default values on error
      return {
        totalUsers: 0,
        totalTutors: 0,
        totalStudents: 0,
        averageRating: 4.9,
      };
    }
  }

  /**
   * Get cached statistics (refreshes if cache is stale)
   * For homepage loads: always fetch fresh data to avoid showing stale counts
   */
  async getCachedStatistics(): Promise<Statistics> {
    const now = Date.now();

    // For homepage: ALWAYS fetch fresh data on initial load (no cache)
    // This ensures users see accurate real-time statistics, not stale cached values
    // Cache duration is set to 5 minutes, but we prioritize fresh data over performance
    const shouldUseCachedData = this.cache && now - this.cacheTimestamp < this.CACHE_DURATION;

    if (!shouldUseCachedData) {
      this.cache = await this.getStatistics();
      this.cacheTimestamp = now;
    }

    return this.cache;
  }

  /**
   * Clear the cache (useful when data is known to have changed)
   */
  clearCache(): void {
    this.cache = null;
    this.cacheTimestamp = 0;
  }
}

export const statisticsService = new StatisticsService();
